import { FC, useRef } from 'react';
import Page, { PageProps } from '@/components/page';
import { Box, Stack } from '@mui/material';
import { DataTableInstance, DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import Icon from '@/components/icon';
import PageHeader from '@/components/page/header';
import DataTable from '@/components/data-table';
import { Navigate, useNavigate } from '@tanstack/react-router';
import Text from '@/components/text';
import { createGroupPolicyV1, getGroupPoliciesV1 } from '@/lib/services/policy-v1.service';
import useModal from '@/hooks/use-modal.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import useToast from '@/hooks/use-toast.hook';
import Button from '@/components/button';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import Tooltip from '@/components/tooltip';
import PageFeatureToast from '@/components/page-feature-toast';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 2, sortable: true },
  { name: 'action', label: '', sortable: false },
];

const PoliciesPage: FC<PageProps> = () => {
  const dtRef = useRef<DataTableInstance>(null);
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { errorToast } = useToast();
  const { canChangePolicy, getTooltip, hasPolicyV2 } = useFeature();

  const handleView = (id: string) => {
    navigate({ to: '/config/group-policies/$policyId', params: { policyId: id } });
  };

  const handleAdd = () => {
    openModal('new-with-name', {
      title: 'Create New Group Policy',
      content: 'Please enter a name for the policy',
      onSubmit: ({ name }: { name: string }) => {
        createGroupPolicyV1(new PolicyModelV1({ name })).then(({ created, model }) => {
          closeModal();
          if (created) {
            navigate({ to: '/config/group-policies/$policyId', params: { policyId: model!.id } });
            return;
          }

          errorToast('The group policy was not created');
        });
      },
    });
  };

  const loadData = async (pageParams: DtPage, filter?: DtFilter) => {
    const { page, pageSize, sort } = pageParams;
    const dataset = await getGroupPoliciesV1(page, pageSize, sort, filter);

    return {
      ...dataset,
      rows: dataset.rows.map(({ id, name, description }) => {
        const hasDescription = !!description;
        return {
          id,
          name: (
            <Box maxWidth="100%">
              <Box>{name}</Box>
              {hasDescription && (
                <Box pt={1}>
                  <Text color="grey" size="small" dotdot>
                    {description}
                  </Text>
                </Box>
              )}
            </Box>
          ),
          action: (
            <Stack direction="row" width="100%" justifyContent="flex-end">
              <Icon name="arrow-right" onClick={() => handleView(id)} />
            </Stack>
          ),
        };
      }),
    };
  };

  if (hasPolicyV2) {
    return <Navigate to="/config/policy/scoped" />;
  }

  return (
    <Page title="Group Policies">
      <PageHeader>
        <PageBlurb>
          Create new group policies, and view and change permissions for existing policies.
        </PageBlurb>
        <PageTools>
          <Tooltip title={getTooltip('add-policy')} disabled={canChangePolicy}>
            <Button
              icon="plus"
              label="Add Policy"
              size="small"
              onClick={handleAdd}
              disabled={!canChangePolicy}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />
      <DomSize>
        <DataTable
          queryKey={QueryKey.GroupPoliciesDataset}
          columns={columns}
          onLoad={loadData}
          ref={dtRef}
          searchPlaceholder="Search by name or description"
          search
          sort={{ columnName: 'name', direction: 'asc' }}
          rowHeight={70}
        />
      </DomSize>
    </Page>
  );
};

export default PoliciesPage;
